import { Component, OnInit, Input } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { of, Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from "rxjs/operators";
import Swal from "sweetalert2";
import { AuthService } from "../../../services/auth.service";
import { UserService } from "../../../services/user.service";
import { IErrorAlert } from "../../../interfaces/error.interface";
import { constants } from "../../../utils/constants";
import { User } from "src/app/models/user.model";

interface IData {
  admin: boolean;
  title: string;
  backUrl: string;
}

@Component({
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  styleUrls: ["./user-management.component.css"],
})
export class UserManagementComponent implements OnInit {
  @Input() data: IData = {
    admin: false,
    title: "Últimos usuarios cargados",
    backUrl: "/pages/home/user-management",
  };

  constants = constants;
  error: IErrorAlert;
  users: User[] = [];
  loading: boolean = true;
  config;
  searchTerm$ = new Subject<string>();

  constructor(
    private _route: ActivatedRoute,
    public _auth: AuthService,
    public _user: UserService
  ) {
    this._route.data.subscribe((data: IData) => (this.data = data));
    if (this._auth.user.isExecutive()) {
      this.data.backUrl = "/pages/home/executive";
    }
  }

  ngOnInit(): void {
    this.getUsers();
    this.initSearchSubscription();
  }

  delete(user: User) {
    this.showDeleteAlert(user).then((confirmed: boolean) => {
      if (confirmed) {
        this.loading = true;
        this._user.delete(user.id).subscribe(
          () => {
            this.loading = false;
            this.getUsers();
            this.showSuccessAlert(user);
          },
          (err) => {
            this.loading = false;
            this.showErrorAlert();
          }
        );
      }
    });
  }

  getUsers() {
    this.loading = true;
    this._user.getUsers(this._auth.user.getUserData().companyId).subscribe(
      (users: User[]) => {
        this.users = users;
        this.setPaginationConfig(users.length);
        this.loading = false;
      },
      (err: IErrorAlert) => {
        this.users = [];
        this.error = err;
        this.loading = false;
      }
    );
  }

  initSearchSubscription() {
    this.searchTerm$
      .pipe(
        tap((searchTerm) => {
          this.users = this._user.users;
          this.error = null;
          if (searchTerm.length !== 0) {
            this.loading = true;
          }
        }),
        filter((searchTerm) => searchTerm.length !== 0),
        debounceTime(500),
        switchMap((term: string) => {
          return this._user.search(term);
        })
      )
      .subscribe((users: User[]) => {
        this.error =
          users.length === 0
            ? constants.alerts.noResults.emptyUserSearch
            : null;
        this.users = users;
        this.loading = false;
      });
  }

  showDeleteAlert(user: User): Promise<boolean> {
    return Swal.fire({
      html:
        "<p class='alert-question'>¿Estás seguro de eliminar al siguiente usuario?</p>" +
        `<h3>${user.firstName} ${user.lastName}</h3>` +
        `<p>Cargo: ${user.charge}</p>`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      showCloseButton: true,
      reverseButtons: true,
      showClass: {
        popup: "animated fadeIn fast",
      },
      hideClass: {
        popup: "animated fadeOut fast",
      },
      customClass: {
        confirmButton: "confirm-button-class",
        cancelButton: "cancel-button-class",
        closeButton: "close-button-class",
      },
    }).then((result) => (result.value ? true : false));
  }

  showSuccessAlert(user: User) {
    Swal.fire({
      showClass: {
        popup: "animated fadeIn fast",
      },
      hideClass: {
        popup: "animated fadeOut fast",
      },
      showCloseButton: true,
      html:
        "<p class='alert-question'>Se ha eliminado con éxito a</p>" +
        `<h3>${user.firstName} ${user.lastName}</h3>` +
        `<p>Cargo: ${user.charge}</p>`,
      icon: "success",
      confirmButtonText: "Cerrar",
      customClass: {
        closeButton: "close-button-class",
        confirmButton: "confirm-button-class",
      },
    });
  }

  showErrorAlert() {
    Swal.fire({
      showClass: {
        popup: "animated fadeIn fast",
      },
      hideClass: {
        popup: "animated fadeOut fast",
      },
      showCloseButton: true,
      html:
        "<p class='alert-question'>Tuvimos un problema, por favor, inténtalo mas tarde.</p>",
      icon: "error",
      confirmButtonText: "Cerrar",
      customClass: {
        closeButton: "close-button-class",
        confirmButton: "confirm-button-class",
      },
    });
  }

  setPaginationConfig(length: number) {
    this.config = {
      id: "custom",
      itemsPerPage: this.data.admin ? 5 : 3,
      currentPage: 1,
      totalItems: length,
    };
  }
}
